import {FlexRow, Modal} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../shared/components/modals/types';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import Util from '../../../shared/util/util';
import DeleteProjectStageMutation from '../../../../mutations/DeleteProjectStageMutation';
import type {StageProfile} from '../../../shared/util/projectStageUtil';
import {isStageHasProjects, isStageLastStage, isStageLastWithProfile} from '../projectStageValidation';
import type {DeleteProjectStageModalCountQuery$data} from './__generated__/DeleteProjectStageModalCountQuery.graphql';

export type DeleteProjectStageModalPayload = {
	/** The company's Relay id; the stage is removed from its projectStages connection. */
	companyId: string;
	/** The stage, with the label the page shows for it. */
	stage: {id: string; label: string; profile: StageProfile | null};
	/** The stage's project count as the page loaded it. */
	projectCount: number;
	/** Whether no other stage has this stage's profile, as the page saw it. */
	lastWithProfile: boolean;
	/** Whether it is the company's only stage, as the page saw it. Its warning takes precedence. */
	lastStage: boolean;
};

export const showDeleteProjectStageModal = (props: DeleteProjectStageModalPayload): void => {
	showModal({
		type: MODAL_TYPE.DELETE_PROJECT_STAGE,
		...props,
	});
};

// Re-read in two cases: when the dialog opens blocked (the stage's projects may have moved out since the page
// loaded) and when the server refuses the delete (projects moved in). Fetching into the store also updates the
// page's rows.
const COUNT_QUERY = graphql`
	query DeleteProjectStageModalCountQuery {
		viewer {
			company {
				projectStages(first: 1000) {
					edges {
						node {
							id
							projectCount
						}
					}
				}
			}
		}
	}
`;

/**
 * Deletes a stage no project is in. Says how many projects block it, and warns first when it is the company's last
 * stage or, otherwise, the last stage with its profile.
 */
export const DeleteProjectStageModal = ({
	closeModal,
	companyId,
	stage,
	projectCount,
	lastWithProfile: lastWithProfileOnOpen,
	lastStage: lastStageOnOpen,
}: ModalProps<DeleteProjectStageModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [count, setCount] = useState(projectCount);
	const [deleting, setDeleting] = useState(false);
	const [lastWithProfile, setLastWithProfile] = useState(lastWithProfileOnOpen);
	const [lastStage, setLastStage] = useState(lastStageOnOpen);
	const {fetch} = useForecastFetchQuery<DeleteProjectStageModalCountQuery$data, Record<string, never>>(COUNT_QUERY);

	// A delete outlives the modal if it is dismissed mid-flight; a late answer must not touch a closed modal.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Shows the stage's count as the server has it now: blocked while projects are in it, the confirm state once
	// none are (they may have moved out since the page loaded or since a refused delete).
	const applyFreshCount = (data: DeleteProjectStageModalCountQuery$data) => {
		const node = data.viewer?.company?.projectStages?.edges
			?.map(edge => edge?.node)
			.find(candidate => candidate?.id === stage.id);
		if (mounted.current) {
			setCount(node?.projectCount ?? 0);
			setDeleting(false);
		}
	};

	// The page's count may be out of date. A blocked dialog re-checks it on open, so projects moved out since the page
	// loaded don't keep it blocked. If the re-check fails the page's count stays: it is still the server's answer.
	useEffect(() => {
		if (projectCount > 0) {
			fetch({}, applyFreshCount);
		}
		// Only on open: `fetch` is a new function on every render.
	}, []);

	const handleError = (error: Error, onFailure: (error: Error) => boolean | void) => {
		const showDefaultError = () => {
			if (onFailure(error) !== true && mounted.current) {
				setDeleting(false);
			}
		};
		if (isStageHasProjects(error)) {
			// If the admin closed the dialog the stage simply stays. Otherwise show the fresh count; if it can't be
			// read, report the refusal through the default error handling rather than guess a count.
			if (mounted.current) {
				fetch({}, applyFreshCount, showDefaultError);
			}
			return;
		}
		if (isStageLastStage(error)) {
			// The other stages went away since the page loaded: warn that it is the last one, then let the admin go ahead.
			if (mounted.current) {
				setLastStage(true);
				setDeleting(false);
			}
			return;
		}
		if (isStageLastWithProfile(error)) {
			// Another stage with this profile went away since the page loaded: warn, then let the admin go ahead.
			if (mounted.current) {
				setLastWithProfile(true);
				setDeleting(false);
			}
			return;
		}
		showDefaultError();
	};

	const handleDeleted = () => {
		if (mounted.current) {
			closeModal();
		}
	};

	const blocked = count > 0;
	const warnLastStage = !blocked && lastStage;
	const warnProfile = !blocked && !lastStage && lastWithProfile && !!stage.profile;
	const warn = warnLastStage || warnProfile;

	const handleDelete = () => {
		if (deleting) {
			return;
		}
		setDeleting(true);
		// Not retried: a retry after a delete that went through would answer that the stage is gone. The go-ahead
		// is sent only when the admin was shown one of the two warnings; it covers both.
		Util.CommitMutation(
			DeleteProjectStageMutation,
			{companyId, id: stage.id, allowLastWithProfile: warn},
			handleDeleted,
			false,
			null,
			handleError
		);
	};

	const profileLabel = stage.profile ? intl.formatMessage({id: `project_status.${stage.profile.toLowerCase()}`}) : '';

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				{intl.formatMessage({
					id: blocked ? 'settings.project_stages.delete.blocked_title' : 'settings.project_stages.delete',
				})}
			</Modal.Title>
			<Modal.Content>
				<div data-cy="project-stage-delete-message">
					{blocked
						? intl.formatMessage({id: 'settings.project_stages.delete.blocked'}, {count, name: stage.label})
						: warnLastStage
						? intl.formatMessage({id: 'settings.project_stages.last_stage'}, {name: stage.label})
						: warnProfile
						? intl.formatMessage(
								{id: 'settings.project_stages.last_with_profile'},
								{name: stage.label, profile: profileLabel}
						  )
						: intl.formatMessage({id: 'settings.project_stages.delete.confirm'}, {name: stage.label})}
				</div>
				<FlexRow>
					{blocked ? (
						<Modal.SecondaryFooterButton onPress={closeModal} dataCy="project-stage-delete-close">
							{intl.formatMessage({id: 'common.close'})}
						</Modal.SecondaryFooterButton>
					) : (
						<>
							<Modal.PrimaryFooterButton
								onPress={handleDelete}
								disabled={deleting}
								dataCy="project-stage-delete-confirm"
							>
								{intl.formatMessage({id: warn ? 'settings.project_stages.delete.anyway' : 'common.delete'})}
							</Modal.PrimaryFooterButton>
							<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
								{intl.formatMessage({id: 'common.cancel'})}
							</Modal.SecondaryFooterButton>
						</>
					)}
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
